/**
 * REST client.
 *
 * Same-origin throughout, so requests are relative and the session cookie rides
 * along without any CORS or credentials configuration.
 */

import type {
  AgentAccount,
  AllowedSender,
  ApiKey,
  ApiKeyScope,
  CreatedApiKey,
  SavedView,
  BrandImage,
  MySettings,
  NotificationPreference,
  PushSubscriptionPayload,
  AppNotification,
  TicketWatcher,
  AgentSignature,
  DashboardFilterOptions,
  DashboardHighlights,
  TicketReport,
  SendFromGrant,
  SendFromOption,
  Automation,
  BankHoliday,
  AwayKind,
  AwayPeriod,
  AwayState,
  BusinessHours,
  CannedResponse,
  Channel,
  ChannelAlert,
  FailedReply,
  ChannelConnections,
  ChannelType,
  EbayConnections,
  ChannelWrite,
  Message,
  Pipeline,
  Team,
  TicketDetail,
  TicketStatus,
  TicketSummary,
  TicketThreadSummary,
  LaneLink,
  LaneStatusInfo,
  LaneBoardOption,
  LaneBoardStatus,
  LaneMember,
  GoogleConnection,
  LaneConnection,
  RelatedTicket,
  User,
  ViewCounts,
  WhatsAppWindow,
} from "../types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * The rest of the error body, for refusals that carry something to act on.
     *
     * Most do not, and callers keep reading `message`. But "that person is
     * already at another company" is only useful if it says which company and
     * which person, so the page can offer to move them rather than making
     * somebody go and find the record themselves.
     */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Exported so `crm-api.ts` shares this transport rather than reimplementing it.
 * Cookies, the `/api` prefix and the ApiError mapping stay in one place.
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
    // Cookies are same-origin, but be explicit so a future origin change fails
    // loudly rather than silently dropping authentication.
    credentials: "same-origin",
  });

  if (!response.ok) {
    let message = response.statusText;
    let details: Record<string, unknown> = {};
    try {
      const body = (await response.json()) as { message?: string };
      if (body.message) message = body.message;
      details = body as Record<string, unknown>;
    } catch {
      // Non-JSON error body; the status text will do.
    }
    throw new ApiError(message, response.status, details);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export type TicketSort =
  | "last_message"
  | "from"
  | "channel"
  | "subject"
  | "id"
  | "assignee"
  | "sla"
  | "pipeline"
  | "status";

export type SortDirection = "asc" | "desc";

/** What a bulk action did: real changes, and the ids it could not touch. */
export interface BulkResult {
  changed: number;
  failed: number[];
}

export interface TicketListParams {
  /**
   * Specific tickets, in place of a question about them.
   *
   * The Drafts view's whole mechanism: which tickets have an unsent reply is
   * known only to this browser, so it names them and gets the ordinary queue
   * back. Empty means empty, not unfiltered.
   */
  ids?: number[];
  channelId?: number;
  channelType?: ChannelType;
  status?: TicketStatus[];
  assignee?: number | "unassigned";
  /** Server-resolved from the session; never a user id from the client. */
  scope?: "me" | "my-team" | "mentions";
  /** A saved view id. Its filters are resolved server-side. */
  savedView?: number;
  /**
   * The live SLA clock, which is what the Overdue view filters on.
   *
   * Computed on the server in JS -- business hours over a holiday calendar has
   * no SQL form -- so a list asking for it pages by offset rather than cursor.
   * See `cursorPaged` in TicketsView.
   */
  slaState?: "breached" | "at_risk" | "ok";
  includeSpam?: boolean;
  /** Show only spam. The Spam view's whole purpose. */
  spamOnly?: boolean;
  pipeline?: number | "unassigned";
  sort?: TicketSort;
  dir?: SortDirection;
  search?: string;
  cursor?: number;
  offset?: number;
  limit?: number;
}

function toQuery(params: TicketListParams): string {
  const search = new URLSearchParams();
  /* One comma-separated parameter, not a repeated key: fifty of them make a
     URL nobody can read in a log. Sorted so the same set of drafts always
     produces the same query key, whatever order the store listed them in. */
  if (params.ids) search.set("ids", [...params.ids].sort((a, b) => a - b).join(","));
  if (params.channelId !== undefined) search.set("channelId", String(params.channelId));
  if (params.channelType) search.set("channelType", params.channelType);
  for (const status of params.status ?? []) search.append("status", status);
  if (params.assignee !== undefined) search.set("assignee", String(params.assignee));
  if (params.search) search.set("search", params.search);
  if (params.scope) search.set("scope", params.scope);
  if (params.savedView) search.set("savedView", String(params.savedView));
  if (params.slaState) search.set("slaState", params.slaState);
  if (params.includeSpam) search.set("includeSpam", "true");
  if (params.spamOnly) search.set("spamOnly", "true");
  if (params.pipeline !== undefined) search.set("pipeline", String(params.pipeline));
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  if (params.cursor !== undefined) search.set("cursor", String(params.cursor));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const query = search.toString();
  return query ? `?${query}` : "";
}

function toFilterQuery(filters: { teamId?: number; userId?: number }): string {
  const search = new URLSearchParams();
  if (filters.teamId) search.set("teamId", String(filters.teamId));
  if (filters.userId) search.set("userId", String(filters.userId));
  const query = search.toString();
  return query ? `?${query}` : "";
}

export const api = {
  me: () => request<User>("/auth/me"),
  /*
   * Sign out of Pulse. The `id` session stands -- leaving Pulse is not leaving
   * Lane -- and identify records that Pulse must be re-authenticated before it
   * is handed another code, so the standing session is not a way back in
   * without a sign-in. See UserMenu.
   */
  logout: () => request<void>("/auth/logout", { method: "POST" }),

  channels: () => request<Channel[]>("/channels"),
  users: () => request<User[]>("/users"),

  /**
   * The platform search box.
   *
   * One request for all four products rather than four from the palette. The
   * fan-out is the server's -- see `global-search.service.ts` -- so a browser
   * that is not sure which product holds the answer does not have to make four
   * round trips to find out.
   */
  globalSearch: (q: string) =>
    request<GlobalSearchResults>(`/search?q=${encodeURIComponent(q)}`),

  viewCounts: () => request<ViewCounts>("/tickets/view-counts"),

  /*
   * `unreadIds` rather than a flag on each ticket: read state belongs to the
   * agent asking, and the ticket summary is shared verbatim with every other
   * agent over the socket.
   */
  tickets: (params: TicketListParams) =>
    request<{
      tickets: TicketSummary[];
      nextCursor: number | null;
      unreadIds: number[];
    }>(`/tickets${toQuery(params)}`),

  ticket: (id: number) => request<TicketDetail>(`/tickets/${id}`),

  sendFrom: () => request<SendFromOption[]>("/tickets/send-from"),

  /** Address suggestions for a recipient field, from contacts we know. */
  recipients: (query: string) =>
    request<{ email: string; name: string | null; companyName: string | null }[]>(
      `/tickets/recipients?q=${encodeURIComponent(query)}`,
    ),

  /**
   * Act on several tickets at once. Managers only, enforced at the route.
   *
   * Each reports what did not work rather than failing the batch, so one
   * ticket that has been merged away does not abandon the other thirty-nine.
   * `changed` counts real changes: a selection already in the target state
   * reports zero rather than claiming work it did not do.
   */
  bulkCloseTickets: (ids: number[]) =>
    request<BulkResult>("/tickets/bulk/close", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),

  bulkAssignTickets: (ids: number[], assigneeId: number | null) =>
    request<BulkResult>("/tickets/bulk/assignee", {
      method: "POST",
      body: JSON.stringify({ ids, assigneeId }),
    }),

  bulkPipelineTickets: (ids: number[], pipelineId: number | null) =>
    request<BulkResult>("/tickets/bulk/pipeline", {
      method: "POST",
      body: JSON.stringify({ ids, pipelineId }),
    }),

  createTicket: (body: {
    toEmail: string;
    toName?: string;
    /** The rest of the To line. The ticket's contact is still `toEmail`. */
    alsoTo?: { email: string; name?: string }[];
    subject: string;
    pipelineId: number;
    channelId: number;
    orderNumber?: string | null;
    quoteNumber?: string | null;
    returnsReference?: string | null;
    laneTask?: string | null;
    /** Copied recipients on the opening email. Ignored without a body. */
    cc?: { email: string; name?: string }[];
    bodyHtml?: string;
    attachments?: {
      storageKey: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
    }[];
  }) =>
    request<TicketSummary>("/tickets", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  setReferences: (
    id: number,
    refs: {
      orderNumber?: string | null;
      quoteNumber?: string | null;
      returnsReference?: string | null;
      laneTask?: string | null;
    },
  ) =>
    request<TicketSummary>(`/tickets/${id}/references`, {
      method: "PATCH",
      body: JSON.stringify(refs),
    }),

  ticketSummary: (id: number) =>
    request<{ enabled: boolean; summary: TicketThreadSummary | null }>(
      `/tickets/${id}/summary`,
    ),

  generateTicketSummary: (id: number) =>
    request<{ enabled: boolean; summary: TicketThreadSummary }>(
      `/tickets/${id}/summary`,
      { method: "POST" },
    ),

  relatedTickets: (id: number) =>
    request<RelatedTicket[]>(`/tickets/${id}/related`),

  setStatus: (id: number, status: TicketStatus) =>
    request<TicketSummary>(`/tickets/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  setAssignee: (id: number, assigneeId: number | null) =>
    request<TicketSummary>(`/tickets/${id}/assignee`, {
      method: "PATCH",
      body: JSON.stringify({ assigneeId }),
    }),

  setSpam: (id: number, isSpam: boolean) =>
    request<TicketSummary>(`/tickets/${id}/spam`, {
      method: "PATCH",
      body: JSON.stringify({ isSpam }),
    }),

  // ── Spam allow list ───────────────────────────────────────────────────────

  allowedSenders: () => request<AllowedSender[]>("/spam/allowed-senders"),

  addAllowedSender: (value: string, note: string | null) =>
    request<AllowedSender>("/spam/allowed-senders", {
      method: "POST",
      body: JSON.stringify({ value, note }),
    }),

  removeAllowedSender: (id: number) =>
    request<{ ok: true }>(`/spam/allowed-senders/${id}`, { method: "DELETE" }),

  /** Rename a ticket. The next reply's subject follows it; sent mail does not. */
  setSubject: (id: number, subject: string) =>
    request<TicketSummary>(`/tickets/${id}/subject`, {
      method: "PATCH",
      body: JSON.stringify({ subject }),
    }),

  setPipeline: (id: number, pipelineId: number | null) =>
    request<TicketSummary>(`/tickets/${id}/pipeline`, {
      method: "PATCH",
      body: JSON.stringify({ pipelineId }),
    }),

  // ── Lane ──────────────────────────────────────────────────────────────────

  laneStatus: () => request<LaneStatusInfo>("/lane/status"),

  laneBoards: () => request<LaneBoardOption[]>("/lane/boards"),

  laneStatuses: (boardId: string) =>
    request<LaneBoardStatus[]>(
      `/lane/statuses?boardId=${encodeURIComponent(boardId)}`,
    ),

  laneMembers: () => request<LaneMember[]>("/lane/members"),


  laneConnection: () => request<LaneConnection>("/lane/connection"),

  /**
   * The agent's own Google grant.
   *
   * Separate from the channel connections above, which authorise shared
   * inboxes. This one is a person: their calendar, and their own mail.
   */
  googleConnection: () => request<GoogleConnection>("/google/connection"),

  disconnectGoogle: () =>
    request<{ ok: true }>("/google/connection", { method: "DELETE" }),

  /** Address suggestions for a meeting's location. Empty when unconfigured. */
  placeSuggestions: (q: string) =>
    request<{ suggestions: { text: string }[] }>(
      `/google/places?q=${encodeURIComponent(q)}`,
    ),

  disconnectLane: () =>
    request<{ ok: true }>("/lane/connection", { method: "DELETE" }),

  laneLink: (ticketId: number) =>
    request<{ enabled: boolean; link: LaneLink | null }>(
      `/lane/tickets/${ticketId}`,
    ),

  createLaneTask: (
    ticketId: number,
    body: {
      title: string;
      description?: string;
      boardId?: string;
      statusId?: string;
      assigneeIds?: string[];
      dueAt?: string;
    },
  ) =>
    request<{ enabled: boolean; link: LaneLink }>(`/lane/tickets/${ticketId}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Dashboard ─────────────────────────────────────────────────────────────

  dashboardFilters: () =>
    request<DashboardFilterOptions>("/dashboard/filters"),

  dashboard: (filters: { teamId?: number; userId?: number }) =>
    request<DashboardHighlights>(`/dashboard${toFilterQuery(filters)}`),

  /*
   * The range is a query string built by the card, not a filter object: a
   * custom range carries two extra parameters that mean nothing for the other
   * presets, and threading them through the shared filter shape would put two
   * always-empty fields on every other call.
   */
  velocity: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
    compareTeam = false,
  ) =>
    request<Velocity>(
      `/dashboard/velocity?${rangeParams(range, scope, {
        compare: compareTeam ? "team" : undefined,
      })}`,
    ),

  /**
   * Replies per closed ticket, per agent, as a median and a tail.
   *
   * Not `velocity`, which is about how *fast* the desk replies. This one is
   * about how many times it has to.
   */
  responsesPerTicket: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
    breakdown: ReplyBreakdown = "none",
  ) =>
    request<ResponsesPerTicket>(
      `/dashboard/responses-per-ticket?${rangeParams(range, scope, {
        breakdown,
      })}`,
    ),

  /**
   * Per-agent ticket counts for the workload card's own date filter.
   *
   * Not `ticketReport`, which answers a fixed week and month. That is why the
   * card's range picker used to do nothing -- it was reading the month slot
   * whatever it was set to.
   */
  agentWorkload: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
  ) =>
    request<AgentWorkload>(`/dashboard/workload?${rangeParams(range, scope)}`),

  trends: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
    compareTeam = false,
  ) =>
    request<Trends>(
      `/dashboard/trends?${rangeParams(range, scope, {
        compare: compareTeam ? "team" : undefined,
      })}`,
    ),

  /**
   * Where the desk is slow, per pipeline, counted in segments.
   *
   * Not `pipelineReport`, which counts tickets raised. A ticket that crossed
   * three desks is one row there and three segments here.
   */
  segmentReport: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
    options: { sort?: string; compareTeam?: boolean } = {},
  ) =>
    request<SegmentReport>(
      `/dashboard/segments?${rangeParams(range, scope, {
        sort: options.sort,
        compare: options.compareTeam ? "team" : undefined,
      })}`,
    ),

  /**
   * The same segments, cut by the agent who answered them.
   *
   * Admin only on the server. The page hides the card for everybody else, but
   * the guard is what makes it a permission rather than a decoration.
   */
  agentSegmentReport: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
    options: { sort?: string } = {},
  ) =>
    request<AgentSegmentReport>(
      `/dashboard/agent-segments?${rangeParams(range, scope, {
        sort: options.sort,
      })}`,
    ),

  kpiMetrics: () => request<KpiCatalogue>("/dashboard/kpi/metrics"),

  kpi: (
    metric: string,
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
  ) =>
    request<KpiResult>(
      `/dashboard/kpi?${rangeParams(range, scope, { metric })}`,
    ),

  /**
   * Which cross-platform integrations are wired up.
   *
   * Asked before the Yay and Lane cards render: an empty chart means "no calls
   * today" if telephony is connected and "no telephony" if it is not, and those
   * are opposite readings of the same blank space.
   */
  telephonyStatus: () =>
    request<{ telephony: { enabled: boolean } }>(
      "/dashboard/integrations",
    ).then((body) => body.telephony),

  /** Call figures from the phone system, for the Calls and workload cards. */
  callsReport: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
  ) => request<CallsReport>(`/dashboard/calls?${rangeParams(range, scope)}`),

  // ── The phone system's call history ───────────────────────────────────────

  phoneCalls: (params: PhoneCallListParams) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === "" || value === null) continue;
      search.set(key, String(value));
    }
    return request<PhoneCallPage>(`/phone-calls?${search.toString()}`);
  },

  phoneCall: (id: number) => request<PhoneCall>(`/phone-calls/${id}`),

  phoneCallsStatus: () => request<PhoneCallsStatus>("/phone-calls/status"),

  /** The phone system's SIP users, for linking them to people. Admin only. */
  sipUsers: () =>
    request<{ enabled: boolean; users: SipUser[] }>("/phone-calls/sip-users"),

  /** Read the history now rather than at the next poll. Admin only. */
  syncPhoneCalls: () =>
    request<{ ok: true }>("/phone-calls/sync", { method: "POST" }),

  phoneCallTranscription: (id: number) =>
    request<{ transcription: PhoneCallTranscription | null }>(
      `/phone-calls/${id}/transcription`,
    ).then((body) => body.transcription),

  /**
   * Where an `<audio>` element plays a call from.
   *
   * A URL rather than a fetch: the API answers with a redirect to the phone
   * system's short-lived link, which the audio element follows and seeks
   * within by itself. Fetching it here would only mean holding the whole
   * recording in memory to hand back as a blob.
   */
  phoneCallRecordingUrl: (id: number) => `/api/phone-calls/${id}/recording`,

  /** Overdue Lane tasks, scoped the same way every report card is. */
  overdueTasks: (scope: ReportScope = {}) =>
    request<OverdueTasks>(`/lane/overdue${toFilterQuery(scope)}`),

  /** Unfinished Lane tasks per person, for the workload card. */
  laneWorkload: (scope: ReportScope = {}) =>
    request<LaneWorkload>(`/lane/workload${toFilterQuery(scope)}`),

  dashboardLayout: (viewKey: string) =>
    request<{ config: DashboardConfig }>(
      `/dashboard/layout?viewKey=${encodeURIComponent(viewKey)}`,
    ),

  saveDashboardLayout: (viewKey: string, config: DashboardConfig) =>
    request<{ config: DashboardConfig }>("/dashboard/layout", {
      method: "PUT",
      body: JSON.stringify({ viewKey, config }),
    }),

  dashboardTemplates: () =>
    request<{ templates: DashboardTemplate[] }>("/dashboard/templates"),

  saveDashboardTemplate: (body: {
    name: string;
    config: DashboardConfig;
    isShared?: boolean;
  }) =>
    request<DashboardTemplate>("/dashboard/templates", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  dashboardTemplate: (id: number) =>
    request<{ config: DashboardConfig }>(`/dashboard/templates/${id}`),

  deleteDashboardTemplate: (id: number) =>
    request<{ ok: true }>(`/dashboard/templates/${id}`, { method: "DELETE" }),

  auditTeams: () => request<AuditTeams>("/dashboard/audit/teams"),

  audit: (teamId: number) =>
    request<AuditResult>(`/dashboard/audit?teamId=${teamId}`),

  /*
   * A fresh five every time this is called, which is why it is never given a
   * `staleTime` by its caller. The weekly audit is the opposite on purpose --
   * see `AuditService`.
   */
  auditSpotCheck: (filters: SpotCheckFilters) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
    return request<SpotCheckResult>(
      `/dashboard/audit/spot-check?${params.toString()}`,
    );
  },

  pipelineReport: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
  ) =>
    request<PipelineReport>(
      `/dashboard/pipeline-report?${rangeParams(range, scope)}`,
    ),

  /**
   * The late tickets behind the closed split, grouped by where it went wrong.
   *
   * A separate call because it is only wanted when somebody opens the row --
   * see `closedLate` on the server.
   */
  closedLate: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
  ) =>
    request<ClosedLate>(
      `/dashboard/closed-report/late?${rangeParams(range, scope)}`,
    ),

  /**
   * The tickets behind a figure on a report card.
   *
   * `dimension` names which grouping the count came from; the server repeats
   * that report's own predicate so the list cannot disagree with the number
   * it was opened from.
   */
  dashboardTickets: (
    dimension: TicketListDimension,
    value: string | undefined,
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
  ) =>
    request<DashboardTickets>(
      `/dashboard/tickets?${rangeParams(range, scope, {
        dimension,
        value: value || undefined,
      })}`,
    ),

  closedReport: (
    range: { preset: string; from?: string; to?: string },
    scope: ReportScope = {},
  ) =>
    request<ClosedReport>(
      `/dashboard/closed-report?${rangeParams(range, scope)}`,
    ),

  ticketReport: (filters: { teamId?: number; userId?: number }) =>
    request<TicketReport>(`/dashboard/ticket-report${toFilterQuery(filters)}`),

  // ── Automations ───────────────────────────────────────────────────────────

  automations: () => request<Automation[]>("/automations"),

  createAutomation: (body: Omit<Automation, "id">) =>
    request<Automation>("/automations", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateAutomation: (id: number, body: Omit<Automation, "id">) =>
    request<Automation>(`/automations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteAutomation: (id: number) =>
    request<{ ok: true }>(`/automations/${id}`, { method: "DELETE" }),

  // ── Canned responses ──────────────────────────────────────────────────────

  cannedResponses: () => request<CannedResponse[]>("/canned-responses"),

  /**
   * A new canned entry, of one of the two kinds.
   *
   * A union rather than one object with both content fields optional, mirroring
   * the two schemas the route validates against: a body snippet must have a
   * body and a subject template must have a subject, and a shape that made both
   * optional would let the caller send neither and find out at runtime.
   *
   * `kind` is absent on the body branch, which is what it means server-side --
   * the column defaults to "body", so every call written before subject
   * templates existed still says exactly what it always said.
   */
  createCannedResponse: (
    body:
      | {
          title: string;
          shortcut: string | null;
          category: string | null;
          bodyHtml: string;
        }
      | {
          title: string;
          shortcut: string | null;
          category: string | null;
          kind: "subject";
          subjectText: string;
        },
  ) =>
    request<CannedResponse>("/canned-responses", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * An edit.
   *
   * `kind` is deliberately not in the accepted shape: the two contents live in
   * different columns, so changing it would point an entry at an empty one, and
   * the route refuses it. Sending the content field that does not match the
   * saved row is a 400 rather than a silent no-op.
   */
  updateCannedResponse: (
    id: number,
    body: Partial<Omit<CannedResponse, "id" | "kind">>,
  ) =>
    request<CannedResponse>(`/canned-responses/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteCannedResponse: (id: number) =>
    request<{ ok: true }>(`/canned-responses/${id}`, { method: "DELETE" }),

  // ── Settings ──────────────────────────────────────────────────────────────

  pipelines: () => request<Pipeline[]>("/settings/pipelines"),

  updatePipeline: (id: number, patch: Partial<Pipeline>) =>
    request<Pipeline>(`/settings/pipelines/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  createPipeline: (body: Omit<Pipeline, "id">) =>
    request<Pipeline>("/settings/pipelines", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Only ever succeeds on a pipeline nothing points at.
   *
   * A pipeline in use answers 409 with the count, because deleting it would
   * take the target out from under tickets whose history refers to it.
   * Deactivating is the fallback, and that is an ordinary `updatePipeline`.
   */
  deletePipeline: (id: number) =>
    request<{ ok: true }>(`/settings/pipelines/${id}`, { method: "DELETE" }),

  teams: () => request<Team[]>("/settings/teams"),

  createTeam: (body: { name: string; slug: string }) =>
    request<Team>("/settings/teams", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteTeam: (id: number) =>
    request<{ ok: true }>(`/settings/teams/${id}`, { method: "DELETE" }),

  setTeamMembers: (id: number, userIds: number[]) =>
    request<{ ok: true }>(`/settings/teams/${id}/members`, {
      method: "PATCH",
      body: JSON.stringify({ userIds }),
    }),

  savedViews: () => request<SavedView[]>("/saved-views"),

  createSavedView: (body: Omit<SavedView, "id" | "ownerUserId" | "position">) =>
    request<SavedView>("/saved-views", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateSavedView: (
    id: number,
    body: Omit<SavedView, "id" | "ownerUserId" | "position">,
  ) =>
    request<SavedView>(`/saved-views/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteSavedView: (id: number) =>
    request<{ ok: true }>(`/saved-views/${id}`, { method: "DELETE" }),

  mySettings: () => request<MySettings>("/me/settings"),

  /**
   * The views this agent has starred, as `/v/:key` segments.
   *
   * Keys and nothing else: the sidebar already holds the presets, the saved
   * views and the agent list, so it resolves each key against those rather
   * than being told a second time what each one is called.
   */
  favourites: () => request<{ keys: string[] }>("/me/favourites"),

  /* PUT, so starring something already starred is not an error -- see the
     controller. The key goes in the path and is validated there. */
  addFavourite: (key: string) =>
    request<{ ok: true }>(`/me/favourites/${encodeURIComponent(key)}`, {
      method: "PUT",
    }),

  removeFavourite: (key: string) =>
    request<{ ok: true }>(`/me/favourites/${encodeURIComponent(key)}`, {
      method: "DELETE",
    }),

  setMyEmoji: (emoji: string | null) =>
    request<{ ok: true }>("/me/emoji", {
      method: "PATCH",
      body: JSON.stringify({ emoji }),
    }),

  /** Where this agent's CRM tasks file to. Null follows their team. */
  setMyBoard: (board: string | null) =>
    request<{ ok: true }>("/me/board", {
      method: "PATCH",
      body: JSON.stringify({ board }),
    }),

  /** What the company list opens on. */
  setMyCompaniesScope: (scope: "all" | "mine") =>
    request<{ ok: true }>("/me/companies-scope", {
      method: "PATCH",
      body: JSON.stringify({ scope }),
    }),

  /** Which Helpdesk view opening the Helpdesk lands on. Null is All open. */
  setMyDefaultView: (view: string | null) =>
    request<{ ok: true }>("/me/default-view", {
      method: "PATCH",
      body: JSON.stringify({ view }),
    }),

  notificationPreferences: () =>
    request<{ preferences: NotificationPreference[] }>(
      "/me/notification-preferences",
    ),

  setNotificationPreference: (
    key: string,
    channel: "app" | "email",
    enabled: boolean,
  ) =>
    request<{ preferences: NotificationPreference[] }>(
      "/me/notification-preferences",
      { method: "PUT", body: JSON.stringify({ key, channel, enabled }) },
    ),

  /**
   * Whether push is set up server-side, and the key needed to subscribe.
   *
   * The key is fetched rather than built into the bundle so a redeploy cannot
   * leave the two disagreeing -- a subscription made against a stale key is one
   * the server can never sign for, and it fails as silence rather than as an
   * error.
   */
  pushStatus: () =>
    request<{ enabled: boolean; publicKey: string | null; devices: number }>(
      "/me/push",
    ),

  subscribePush: (subscription: PushSubscriptionPayload) =>
    request<{ ok: true }>("/me/push", {
      method: "POST",
      body: JSON.stringify(subscription),
    }),

  unsubscribePush: (endpoint: string) =>
    request<{ ok: true }>("/me/push", {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    }),

  notifications: () =>
    request<{ items: AppNotification[]; unread: number }>("/notifications"),

  /**
   * A page of the history, newest first.
   *
   * `before` is the id to read backwards from, not an offset: notifications
   * arrive while somebody is reading, and an offset would show them a row
   * twice and skip another.
   */
  notificationHistory: (options: { product?: string; before?: number } = {}) => {
    const query = new URLSearchParams();
    if (options.product) query.set("product", options.product);
    if (options.before) query.set("before", String(options.before));
    const suffix = query.size > 0 ? `?${query}` : "";

    return request<{ items: AppNotification[]; nextCursor: number | null }>(
      `/notifications/history${suffix}`,
    );
  },

  markNotificationsRead: (ids?: number[]) =>
    request<{ ok: true }>("/notifications/read", {
      method: "POST",
      body: JSON.stringify(ids ? { ids } : {}),
    }),

  clearNotifications: () =>
    request<{ ok: true }>("/notifications", { method: "DELETE" }),

  ticketWatchers: (ticketId: number) =>
    request<{ watchers: TicketWatcher[]; watching: boolean }>(
      `/tickets/${ticketId}/watchers`,
    ),

  setTicketWatching: (ticketId: number, watching: boolean) =>
    request<{ watchers: TicketWatcher[]; watching: boolean }>(
      `/tickets/${ticketId}/watchers`,
      { method: watching ? "POST" : "DELETE" },
    ),

  setMyAvatar: (patch: { color: string | null }) =>
    request<{ ok: true }>("/me/avatar", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  apiKeys: () => request<ApiKey[]>("/api-keys"),

  /** The response is the only place the token ever appears. Do not cache it. */
  createApiKey: (input: {
    name: string;
    scope: ApiKeyScope;
    attributeToKey: boolean;
    expiresInDays: number | null;
  }) =>
    request<CreatedApiKey>("/api-keys", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  revokeApiKey: (id: number) =>
    request<void>(`/api-keys/${id}`, { method: "DELETE" }),


  /**
   * The signature that will go on this agent's reply, already resolved: their
   * own if they have one, otherwise the alias's, with placeholders filled in.
   */
  mySignature: (channelId?: number) =>
    request<{ signatureHtml: string | null; blockHtml: string | null }>(
      channelId === undefined
        ? "/me/signature"
        : `/me/signature?channelId=${channelId}`,
    ),

  brandImages: () => request<BrandImage[]>("/settings/images"),

  uploadBrandImage: async (file: File) => {
    const response = await fetch("/api/settings/images", {
      method: "POST",
      headers: {
        "content-type": file.type,
        "x-filename": encodeURIComponent(file.name),
      },
      body: file,
      credentials: "same-origin",
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      throw new ApiError(body.message ?? "Upload failed", response.status);
    }
    return response.json() as Promise<BrandImage>;
  },

  /**
   * Copy an image the desk does not host into its library.
   *
   * Fetched server-side rather than here: the browser cannot read a
   * cross-origin image it did not request, and the URLs in a pasted signature
   * are on somebody else's server by definition.
   */
  importBrandImage: (url: string, name?: string) =>
    request<BrandImage>("/settings/images/import", {
      method: "POST",
      body: JSON.stringify({ url, name }),
    }),

  deleteBrandImage: (id: number) =>
    request<{ ok: true }>(`/settings/images/${id}`, { method: "DELETE" }),

  agents: () => request<AgentAccount[]>("/settings/agents"),

  inviteAgent: (body: { email: string; name: string; role: "agent" | "admin" }) =>
    request<{ id: number }>("/settings/agents", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateAgent: (
    id: number,
    patch: {
      name?: string;
      role?: "agent" | "admin";
      isActive?: boolean;
      /** Null clears it. Refused with a 409 if another agent holds it. */
      extension?: string | null;
      /** A SIP user's uuid from `sipUsers`. Null unlinks. 409 if taken. */
      yayUserUuid?: string | null;
    },
  ) =>
    request<{ ok: true }>(`/settings/agents/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  sendFromGrants: () => request<SendFromGrant[]>("/settings/send-from"),

  setSendFromGrant: (
    id: number,
    channelIds: number[],
    defaultChannelId: number | null,
  ) =>
    request<{ ok: true }>(`/settings/send-from/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ channelIds, defaultChannelId }),
    }),

  signatures: () => request<AgentSignature[]>("/settings/signatures"),

  setSignature: (id: number, signatureHtml: string | null) =>
    request<{ ok: true }>(`/settings/signatures/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ signatureHtml }),
    }),

  businessHours: () => request<BusinessHours>("/settings/business-hours"),

  away: () => request<AwayState>("/away"),

  startAway: (kind: AwayKind) =>
    request<AwayPeriod>("/away", {
      method: "POST",
      body: JSON.stringify({ kind }),
    }),

  /* POST rather than DELETE, and the route says so: coming back early ends the
     period, it does not remove it. The row is what stops a second lunch. */
  endAway: (id: number) =>
    request<AwayPeriod>(`/away/${id}/end`, { method: "POST" }),

  createHoliday: (body: { date: string; name: string }) =>
    request<BankHoliday>("/settings/bank-holidays", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteHoliday: (id: number) =>
    request<{ ok: true }>(`/settings/bank-holidays/${id}`, { method: "DELETE" }),

  moveChannel: (id: number, channelId: number) =>
    request<TicketSummary>(`/tickets/${id}/channel`, {
      method: "PATCH",
      body: JSON.stringify({ channelId }),
    }),

  reply: (
    id: number,
    body: {
      bodyHtml: string;
      /** Forwarding: send to these people rather than whoever wrote last. */
      to?: { name?: string; email: string }[];
      /** Forwarding: "Fwd: …" in place of the ticket's own "Re: …". */
      subject?: string;
      /** Forwarding: mark the stored message, so the thread can say so. */
      isForward?: boolean;
      cc?: { name?: string; email: string }[];
      attachments?: {
        storageKey: string;
        filename: string;
        mimeType: string;
        sizeBytes: number;
      }[];
      /**
       * Forwarding: files already on this ticket, sent again with the message.
       *
       * Ids, not keys. An inbound Gmail attachment may have no key at all until
       * somebody opens it, so the server resolves these and mirrors what it has
       * to -- see `ReplyService.#withCopiedFiles`.
       */
      copyAttachmentIds?: number[];
      thenStatus?: "open" | "closed";
      fromChannelId?: number;
      /** The body already ends with the signature the composer showed. */
      signatureIncluded?: boolean;
    },
  ) =>
    request<{ messageId: number }>(`/tickets/${id}/reply`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Where the 24-hour WhatsApp window stands on a ticket.
   *
   * Answers for any ticket, with `applicable: false` off a channel that has no
   * such thing, so the composer can ask unconditionally rather than knowing
   * which channels have windows.
   */
  whatsappWindow: (id: number) =>
    request<WhatsAppWindow>(`/tickets/${id}/whatsapp-window`),

  addNote: (id: number, bodyHtml: string) =>
    request<{ messageId: number }>(`/tickets/${id}/notes`, {
      method: "POST",
      body: JSON.stringify({ bodyHtml }),
    }),

  /** Correct your own note. The server re-checks author and age; see notes.service. */
  editNote: (ticketId: number, messageId: number, bodyHtml: string) =>
    request<{ editedAt: string }>(`/tickets/${ticketId}/notes/${messageId}`, {
      method: "PATCH",
      body: JSON.stringify({ bodyHtml }),
    }),

  /**
   * Send a failed reply again, unchanged.
   *
   * Addressed by message, not ticket: a conversation can hold more than one
   * failed reply. The server refuses anything that is not `failed` -- see
   * ReplyService.retry -- so this cannot re-send a message still in flight.
   */
  retrySend: (messageId: number) =>
    request<{ ok: true }>(`/tickets/messages/${messageId}/retry`, {
      method: "POST",
    }),

  /**
   * Upload raw bytes for an outbound attachment.
   *
   * Sent as a raw body with the filename in a header rather than as multipart:
   * there is exactly one file per request, so multipart would add a parser on
   * both ends for no benefit.
   */
  upload: async (file: File) => {
    const response = await fetch("/api/uploads", {
      method: "POST",
      headers: {
        "content-type": file.type || "application/octet-stream",
        "x-filename": encodeURIComponent(file.name),
      },
      body: file,
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new ApiError(`Upload failed: ${response.statusText}`, response.status);
    }

    return (await response.json()) as {
      storageKey: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
    };
  },

  channelAlerts: () => request<ChannelAlert[]>("/channels/alerts"),

  failedReplies: () => request<FailedReply[]>("/channels/failed-replies"),

  /** Settings' "Retry push": a refusal comes back as `registered: false`. */
  registerWatch: (id: number) =>
    request<{ registered: boolean; error: string | null }>(
      `/channels/${id}/watch`,
      { method: "POST" },
    ),

  syncChannel: (id: number) =>
    request<{ queued: boolean }>(`/channels/${id}/sync`, { method: "POST" }),

  createChannel: (input: ChannelWrite) =>
    request<Channel>("/channels", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateChannel: (id: number, patch: Partial<Omit<ChannelWrite, "type" | "address">>) =>
    request<Channel>(`/channels/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deactivateChannel: (id: number) =>
    request<Channel & { retainedTickets: number }>(
      `/channels/${id}/deactivate`,
      { method: "POST" },
    ),

  activateChannel: (id: number) =>
    request<Channel>(`/channels/${id}/activate`, { method: "POST" }),

  /*
   * Gmail authorisation. There is deliberately no `connectChannel` here: the
   * consent flow has to be a top-level navigation to
   * /api/channels/:id/oauth/start, so the UI uses a plain anchor. A fetch would
   * follow the redirect to Google inside XHR and fail on CORS.
   */
  channelConnections: () =>
    request<ChannelConnections>("/channels/connections"),

  disconnectChannel: (id: number) =>
    request<{ connected: boolean }>(`/channels/${id}/oauth/disconnect`, {
      method: "POST",
    }),

  ebayConnections: () => request<EbayConnections>("/channels/ebay/connections"),

  connectEbayToken: (
    id: number,
    input: { refreshToken: string; refreshTokenExpiresAt: string | null },
  ) =>
    request<unknown>(`/channels/${id}/ebay/token`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  disconnectEbay: (id: number) =>
    request<{ connected: boolean; note: string }>(`/channels/${id}/ebay/disconnect`, {
      method: "POST",
    }),
};

export type { Message };

/** Who a report is about. Empty means the whole desk. */
/**
 * Who and what a report is narrowed to.
 *
 * Two layers, deliberately kept in one object. `teamId` and `userId` are the
 * *page's* scope -- whose dashboard is being read -- and are the same for every
 * card on it. `pipelineIds` and `agentIds` are one card's own filters. They
 * intersect on the server, which is what makes "the Support team's Quote
 * tickets" mean what it looks like.
 *
 * They ride together because they end up in the same query string and, more
 * importantly, in the same query key: a card whose filter changed has to
 * refetch, and threading the two separately is how one of them gets left out
 * of a key and a card quietly stops updating.
 */
export interface ReportScope {
  teamId?: number;
  userId?: number;
  /** A card's Pipeline filter. Empty or absent means every pipeline. */
  pipelineIds?: number[];
  /** A card's Agent filter; 0 is "unassigned". Empty means every agent. */
  agentIds?: number[];
}

function rangeParams(
  range: { preset: string; from?: string; to?: string },
  scope: ReportScope = {},
  extra: Record<string, string | undefined> = {},
): string {
  const params = new URLSearchParams({ range: range.preset });
  if (range.preset === "custom" && range.from && range.to) {
    params.set("from", range.from);
    params.set("to", range.to);
  }
  /*
   * Folded in here rather than left to each card: forgetting the scope on one
   * of them would show the whole desk's figures under an agent's name, which is
   * the kind of wrong that looks right.
   */
  if (scope.userId) params.set("userId", String(scope.userId));
  if (scope.teamId) params.set("teamId", String(scope.teamId));
  /* Comma-separated rather than repeated, because Express hands back a string
     for one value and an array for two -- see `idList` on the server. An empty
     selection is no filter, so it is left out entirely. */
  if (scope.pipelineIds?.length) {
    params.set("pipelineIds", scope.pipelineIds.join(","));
  }
  if (scope.agentIds?.length) {
    params.set("agentIds", scope.agentIds.join(","));
  }

  /*
   * The team comparison only means anything about one agent. Dropped otherwise
   * rather than sent and ignored, so the query key -- and therefore the cache
   * entry -- does not differ between two requests that return the same thing.
   */
  for (const [key, value] of Object.entries(extra)) {
    if (value === undefined) continue;
    if (key === "compare" && !scope.userId) continue;
    params.set(key, value);
  }

  return params.toString();
}

export interface MetricDistribution {
  count: number;
  medianMinutes: number | null;
  riskCount: number;
  p90Minutes: number | null;
  p95Minutes: number | null;
}

interface VelocitySide {
  /** Every reply, first or later -- what the velocity card shows. */
  overall: MetricDistribution;
  firstResponse: MetricDistribution;
  nextResponse: MetricDistribution;
  sla: { measured: number; met: number; rate: number | null };
}

export interface Velocity {
  range: { from: string; to: string; preset: string };
  inProgress: boolean;
  current: VelocitySide;
  previous: VelocitySide;
  slaDelta: number | null;
  /** One aggregate for the agent's team, or null when not asked for. */
  teamAverage: VelocitySide | null;
}

/**
 * What a reply-count figure can be cut by.
 *
 * No priority: a Pulse ticket has no priority field, so there is nothing to
 * group by. See `responses.ts` on the server.
 */
export type ReplyBreakdown = "none" | "pipeline" | "channel";

export interface ReplyDistribution {
  /** Closed tickets behind the figures. Always shown beside them. */
  tickets: number;
  /** Null when nothing closed, never 0 -- zero replies is a real answer. */
  median: number | null;
  p90: number | null;
  p95: number | null;
}

export interface AgentReplies {
  agentId: number | null;
  agentName: string;
  overall: ReplyDistribution;
  /** Empty unless a breakdown was asked for. */
  breakdown: { key: string; label: string; distribution: ReplyDistribution }[];
}

export interface ResponsesPerTicket {
  range: { from: string; to: string; preset: string };
  inProgress: boolean;
  breakdown: ReplyBreakdown;
  agents: AgentReplies[];
  /** True when the period held more closures than one scan reads. */
  truncated: boolean;
}

export interface Trends {
  range: { from: string; to: string; preset: string };
  granularity: "hourly" | "daily" | "bi-weekly" | "monthly";
  points: {
    from: string;
    to: string;
    overall: number | null;
    firstResponse: number | null;
    nextResponse: number | null;
    count: number;
    /** Present only when the card asked to be compared against the team. */
    teamAverage?: number | null;
  }[];
  comparedWithTeam: boolean;
}

/** One pipeline's share of the response cycles answered on it. */
export interface SegmentReport {
  range: { from: string; to: string; preset: string };
  sort: string;
  pipelines: PipelineSegmentRow[];
  /** One aggregate line for the agent's team, or null when not asked for. */
  teamAverage: PipelineSegmentRow[] | null;
}

export interface PipelineSegmentRow {
  pipelineId: number | null;
  name: string;
  /** The chip colour a manager chose, where the pipeline still exists. */
  color: string | null;
  /** This pipeline's own allowance in business minutes, or null if untimed. */
  targetMinutes: number | null;
  segments: number;
  /** Distinct tickets behind those segments -- never the SLA denominator. */
  tickets: number;
  medianMinutes: number | null;
  riskCount: number;
  p90Minutes: number | null;
  p95Minutes: number | null;
  measured: number;
  met: number;
  slaRate: number | null;
}

/**
 * Response cycles cut by whoever answered them, rather than by queue.
 *
 * The same segments as `SegmentReport` and the same arithmetic, so the two can
 * be read against each other. Admin only.
 */
export interface AgentSegmentReport {
  range: { from: string; to: string; preset: string };
  sort: string;
  agents: AgentSegmentRow[];
}

export interface AgentSegmentRow {
  /** Null for the bucket nothing could be attributed to. */
  userId: number | null;
  /** The agent's name, "Unattributed", or an id for somebody since deleted. */
  name: string;
  segments: number;
  /** Distinct tickets behind those segments -- never the SLA denominator. */
  tickets: number;
  medianMinutes: number | null;
  riskCount: number;
  p90Minutes: number | null;
  p95Minutes: number | null;
  measured: number;
  met: number;
  slaRate: number | null;
}

/** What a KPI slot may be set to. Served, so the labels live in one place. */
export interface KpiCatalogue {
  metrics: KpiDefinition[];
  defaults: string[];
}

export interface KpiDefinition {
  id: string;
  label: string;
  unit: "count" | "rate" | "minutes";
  higherIsBetter: boolean;
  /** A figure about this instant, so it has no previous period and no arrow. */
  live: boolean;
  caption?: string;
}

export interface KpiResult {
  metric: string;
  value: number | null;
  previous?: number | null;
  /** Percentage points for a rate, per cent for everything else. */
  delta?: number | null;
  /** The population behind the value, where it is narrower than the whole. */
  measured?: number;
}

/** How one card has been set up. Every field optional -- see the API's notes. */
export interface DashboardCardConfig {
  chart?: string;
  palette?: string;
  colors?: Record<string, string>;
  range?: { preset: string; from?: string; to?: string };
  metric?: string;
  /** How the card cuts its figure. See `ResponsesPerTicket`. */
  breakdown?: string;
  /**
   * The card's own Pipeline and Agent filters, as ids.
   *
   * Multi-select, and an OR within each: "Quote or Support". Across the two it
   * is an AND, which is what makes "Support's Quote tickets" mean what it
   * looks like. An absent or empty list is no filter at all rather than a
   * filter matching nothing -- an empty checklist has not been used yet.
   *
   * Stored with the rest of a card's arrangement, so a filter somebody set
   * survives a reload and follows them to another machine. It is *not* a
   * scope: these name pipelines and agents, and what a given person may
   * actually read is settled by `DashboardScopeGuard` whatever is stored here
   * -- which is why a template carrying one is still safe to hand over.
   */
  pipelineIds?: number[];
  /** Zero means "unassigned". See `UNASSIGNED_AGENT`. */
  agentIds?: number[];
  collapsed?: boolean;
}

/**
 * One person's arrangement of one dashboard view.
 *
 * Carries no scope, and cannot: that is what lets a template be handed to a
 * colleague without handing over a view of anybody's numbers.
 */
export interface DashboardConfig {
  order?: string[];
  cards?: Record<string, DashboardCardConfig>;
}

export interface OverdueTasks {
  /** False when Lane is not configured. The card renders nothing at all. */
  enabled: boolean;
  groups: {
    /** The board it sits on -- what a person calls "Courier issues". */
    board: string;
    tasks: {
      id: string;
      title: string;
      dueAt: string;
      assigneeNames: string[];
      /** Minutes past due, so the card can say hours as well as days. */
      minutesLate: number;
      url: string;
    }[];
  }[];
  total: number;
  /** Boards Lane would not return, so a short count is never silent. */
  unreadable: number;
  error?: string;
}

export interface LaneWorkload {
  /** False when Lane is not configured. The section renders nothing at all. */
  enabled: boolean;
  /**
   * Everyone with tasks on the boards read, heaviest pile first.
   *
   * Somebody with `open: 0` has finished theirs and is shown as up to date;
   * somebody Lane has never assigned anything is simply absent, which is the
   * difference the card has to keep visible.
   */
  people: {
    name: string;
    email: string | null;
    open: number;
    total: number;
  }[];
  /** Boards Lane would not return, so a short list is never silent. */
  unreadable: number;
  error?: string;
}

export interface DashboardTemplate {
  id: number;
  name: string;
  ownerUserId: number;
  isShared: boolean;
  config: DashboardConfig;
  isMine: boolean;
}

export interface PipelineReport {
  range: { from: string; to: string; preset: string };
  total: number;
  breakdown: {
    pipelineId: number;
    name: string;
    color: string;
    count: number;
    previous: number;
    share: number;
  }[];
  /** Tickets with no pipeline: triage debt, never a category. */
  needsPipeline: number;
  mergedCount: number;
}

export interface ClosedReport {
  range: { from: string; to: string; preset: string };
  granularity: string;
  /**
   * The same split over time, for the line-split view.
   *
   * Counts, so a quiet bucket is a zero rather than a break: "nothing was
   * closed" is a real reading, unlike a median of nothing.
   */
  points: { from: string; to: string; onTime: number; late: number }[];
  closed: number;
  onTime: number;
  late: number;
  /** Closed with no response target, so outside the on-time split. */
  unmeasured: number;
  onTimeRate: number | null;
  previousRate: number | null;
  pointsDelta: number | null;
  mergedCount: number;
  /** Closed before resolution times were recorded; datable to no period. */
  unrecorded: number;
}

export interface AuditTeams {
  /** Monday of the week being audited. */
  weekStart: string;
  teams: { id: number; name: string }[];
}

/**
 * Why the late tickets were late.
 *
 * Every late ticket is in exactly one group and the groups sum to `late` on
 * the report above -- see `lateBreakdown` on the server for why that is a hard
 * requirement rather than a tidiness.
 */
export interface ClosedLate {
  range: { from: string; to: string; preset: string };
  truncated?: boolean;
  groups: {
    /** A pipeline's name, or "Multiple pipelines". */
    key: string;
    count: number;
    /** `routePath` is set only in the multiple-pipelines group. */
    tickets: { ticketId: number; routePath: string | null }[];
  }[];
}

/** Which figure a ticket list is opening from. Mirrors the server's list. */
export type TicketListDimension =
  | "raised-pipeline"
  | "raised-agent"
  | "answered-pipeline"
  | "answered-agent"
  | "closed-on-time"
  | "closed-late"
  | "closed-agent";

export interface DashboardTickets {
  /** True when the list was capped, so it does not read as the whole answer. */
  truncated: boolean;
  tickets: {
    id: number;
    subject: string;
    status: string;
    assigneeName: string | null;
    pipelineName: string | null;
  }[];
}

// ── Telephony ───────────────────────────────────────────────────────────────

export type PhoneCallDirection = "inbound" | "outbound" | "internal" | "unknown";

export interface PhoneCallListParams {
  from?: string;
  to?: string;
  direction?: PhoneCallDirection;
  outcome?: "answered" | "missed";
  recorded?: boolean;
  /** A Pulse user's calls; 0 is calls on extensions linked to nobody. */
  agentId?: number;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface PhoneCall {
  id: number;
  startedAt: string;
  endedAt: string | null;
  /** Talk time, ringing excluded. Zero for a call nobody answered. */
  durationSeconds: number;
  direction: PhoneCallDirection;
  /** The phone system's word for how it ended. */
  disposition: string | null;
  answered: boolean;
  /** Inbound, and nobody picked up. */
  missed: boolean;
  from: { number: string | null; name: string | null };
  to: { number: string | null; name: string | null };
  /** The outside party, matched to the CRM by number. Null on an internal call. */
  remote: {
    number: string;
    area: string | null;
    contact: { id: number; name: string | null } | null;
    company: { id: number; name: string } | null;
  } | null;
  /** Our number an inbound call rang, in E.164. */
  line: string | null;
  agent: {
    userId: number | null;
    name: string | null;
    extension: string | null;
    /** False until somebody sets this extension on a Pulse user. */
    linked: boolean;
  } | null;
  hasRecording: boolean;
  transcriptionStatus: string | null;
  /** Whether this viewer may play the recording and read the transcript. */
  canHear: boolean;
}

export interface SipUser {
  uuid: string;
  /** The phone system's display name for them. */
  name: string | null;
  extension: string | null;
  /** Who in Pulse this SIP user is linked to, if anybody. */
  linkedUser: { id: number; name: string } | null;
}

export interface PhoneCallPage {
  calls: PhoneCall[];
  total: number;
  page: number;
  pageSize: number;
}

export type PhoneCallsStatus =
  | { enabled: false }
  | {
      enabled: true;
      syncedThrough: string | null;
      lastPassAt: string | null;
      failing: boolean;
      /** Admins only. */
      lastError?: string | null;
      lastErrorAt?: string | null;
    };

export interface PhoneCallTranscription {
  status: string | null;
  summary: string | null;
  actionItems: { description: string; owner: string | null; dueDate: string | null }[];
  utterances: { speaker: string | null; start: number | null; text: string }[];
  language: string | null;
}

export interface CallTotals {
  inbound: number;
  inboundAnswered: number;
  missed: number;
  missedRate: number | null;
  outbound: number;
  outboundAnswered: number;
  internal: number;
  talkSeconds: number;
  medianSeconds: number | null;
}

export type CallsReport =
  | { enabled: false }
  | {
      enabled: true;
      range: { from: string; to: string; preset: string };
      inProgress: boolean;
      granularity: string;
      /** Whether missed calls to a line -- nobody's in particular -- are counted. */
      includesLineCalls: boolean;
      capped: boolean;
      totals: CallTotals;
      previous: CallTotals;
      byAgent: {
        userId: number | null;
        /** The SIP user's uuid, which Settings links a person by. */
        platformUserId: string | null;
        extension: string | null;
        name: string;
        linked: boolean;
        taken: number;
        made: number;
        connected: number;
        missed: number;
        talkSeconds: number;
        medianSeconds: number | null;
      }[];
      points: { from: string; taken: number; missed: number; made: number }[];
    };

export interface AgentWorkload {
  range: { from: string; to: string; preset: string };
  inProgress: boolean;
  /**
   * Heaviest first, with the same span of the previous period beside it.
   *
   * `key` is the agent's name, which is what the chart draws and what the
   * colour is stored against; `userId` rides alongside so a count can open
   * into the tickets behind it. Null for the unassigned row.
   */
  byAgent: {
    key: string;
    count: number;
    previous: number;
    userId: number | null;
  }[];
}

export interface AuditRow {
  ticketId: number;
  subject: string;
  assignee: { id: number; name: string } | null;
  missedSla: boolean;
  /** Null when unread, or when no model is configured. */
  sentiment: "happy" | "neutral" | "angry" | null;
  sentimentRationale: string | null;
}

export interface AuditResult {
  weekStart: string;
  rows: AuditRow[];
  /**
   * Team members the week's sample could not reach.
   *
   * The draw takes one ticket per member, so anybody here had none raised to
   * them last week. Named rather than counted, because "Priya and Tom" is
   * something a manager can act on and "2 uncovered" is not.
   */
  uncovered: { id: number; name: string }[];
}

export interface SpotCheckFilters {
  userId?: number;
  pipelineId?: number;
  search?: string;
  range?: string;
  from?: string;
  to?: string;
}

export interface SpotCheckResult {
  rows: AuditRow[];
  /** The window that was actually searched, or null for all of it. */
  from: string | null;
  to: string | null;
}

/**
 * One row in the platform search box, already reduced to what a palette draws.
 *
 * Deliberately not four different shapes. A ticket, a company, a call and a
 * meeting have almost nothing in common as records, but they have exactly this
 * much in common as *destinations*, and a list that renders one shape is a list
 * whose rows cannot drift apart. What differs between the groups is their
 * heading and their icon, which the palette holds.
 *
 * `href` is built on the server for the same reason. Which view can show any
 * ticket, and whether a call lives under `/sales/calls/:id`, are facts about
 * routing that the search already has to know to decide what to return.
 */
export interface GlobalSearchHit {
  id: number;
  title: string;
  subtitle: string | null;
  href: string;
}

export interface GlobalSearchResults {
  companies: GlobalSearchHit[];
  tickets: GlobalSearchHit[];
  calls: GlobalSearchHit[];
  meetings: GlobalSearchHit[];
}
